import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PillBottle, CheckCircle2, AlertTriangle, Clock, Eye, History, Printer, FileSignature, ExternalLink } from "lucide-react";

// Short labels for the Staff Profile card summary. The full outcome text
// is displayed in the assessment form and PDF, not in the summary card.
const OUTCOME_LABEL = {
  competent: "Competent",
  requires_supervision: "Further supervision required",
  not_yet_competent: "Competency not yet demonstrated",
};

const OUTCOME_TONE = {
  competent: "border-emerald-300 bg-emerald-50 text-emerald-900",
  requires_supervision: "border-amber-300 bg-amber-50 text-amber-900",
  not_yet_competent: "border-red-300 bg-red-50 text-red-800",
};

function reviewState(reviewDate) {
  if (!reviewDate) return { label: "No review date", tone: "text-slate-500" };
  const d = new Date(reviewDate);
  const delta = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (delta < 0) return { label: `Overdue by ${Math.abs(delta)}d`, tone: "text-red-700 font-semibold" };
  if (delta <= 30) return { label: `Due in ${delta}d`, tone: "text-amber-800" };
  return { label: `In ${delta}d`, tone: "text-slate-600" };
}

export default function MedicationCompetencyCard({ staffId, staffName, canManage }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/medication-competency/staff/${staffId}/history`);
      setHistory(r.data?.items || []);
    } catch (e) {
      // 403 is expected for care workers looking at someone else — hide the card
      if (e.response?.status !== 403) toast.error(formatApiError(e));
      setHistory([]);
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const latest = history.find((h) => h.read_only) || null;
  const outstandingActions = (latest?.actions || []).filter((a) => !["completed", "verified"].includes(a.status));
  const rs = reviewState(latest?.review_date || latest?.reassessment_date);

  const print = async (id) => {
    try {
      const r = await api.get(`/medication-competency/${id}/print`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="staff-med-competency-card">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">People · Competency</div>
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><PillBottle className="w-4 h-4 text-cyan-700" /> Medication Competency</h3>
        </div>
        <Link to="/medication-competency" className="text-[11px] text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1" data-testid="link-open-med-competency">
          Open module <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {loading ? (
        <div className="p-4 text-xs text-slate-400 italic">Loading…</div>
      ) : !latest ? (
        <div className="p-4 text-xs text-slate-500 flex items-center gap-2" data-testid="med-competency-empty">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          No completed medication competency assessment on file for {staffName}.
          {canManage && (
            <Link to="/medication-competency" className="ml-auto text-cyan-700 hover:underline text-[11px] inline-flex items-center gap-1">
              <FileSignature className="w-3 h-3" /> Start assessment
            </Link>
          )}
        </div>
      ) : (
        <div className="p-4 space-y-3" data-testid="med-competency-latest">
          <div className={`border rounded-md p-3 flex items-center justify-between ${OUTCOME_TONE[latest.outcome] || ""}`}>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">Latest Outcome</div>
              <div className="text-sm font-semibold">{OUTCOME_LABEL[latest.outcome] || "—"}</div>
              <div className="text-[11px] opacity-80 mt-0.5">
                Assessed {latest.assessment_date} · by {latest.assessor_name || "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">Next Review</div>
              <div className={`text-sm ${rs.tone}`}><Clock className="w-3 h-3 inline mr-1" />{rs.label}</div>
              {latest.review_date || latest.reassessment_date ? (
                <div className="text-[10px] opacity-70">{latest.review_date || latest.reassessment_date}</div>
              ) : null}
            </div>
          </div>

          {outstandingActions.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/60 rounded p-2 text-xs" data-testid="med-outstanding-actions">
              <div className="font-semibold text-amber-900 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {outstandingActions.length} outstanding action(s)</div>
              <ul className="list-disc pl-4 space-y-0.5 text-amber-900/90">
                {outstandingActions.slice(0, 3).map((a) => (
                  <li key={a.id}>{a.action || "—"}{a.target_date ? ` — target ${a.target_date}` : ""}</li>
                ))}
                {outstandingActions.length > 3 && <li className="italic opacity-70">…and {outstandingActions.length - 3} more</li>}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <Link to="/medication-competency" className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 hover:underline" data-testid="btn-view-latest">
              <Eye className="w-3 h-3" /> View assessment
            </Link>
            <button onClick={() => print(latest.id)} className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 hover:underline" data-testid="btn-print-latest">
              <Printer className="w-3 h-3" /> Print
            </button>
            <button onClick={() => setExpanded((v) => !v)} className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 hover:underline" data-testid="btn-toggle-history">
              <History className="w-3 h-3" /> {expanded ? "Hide history" : `View history (${history.length})`}
            </button>
          </div>

          {expanded && (
            <ul className="border-t border-slate-100 pt-2 space-y-1 text-xs" data-testid="med-history-list">
              {history.map((h) => (
                <li key={h.id} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-b-0">
                  <div>
                    <span className="text-slate-800">{h.assessment_date || "—"}</span>
                    <span className="text-slate-500 mx-1">·</span>
                    <span className={h.outcome === "competent" ? "text-emerald-800" : h.outcome === "requires_supervision" ? "text-amber-900" : h.outcome === "not_yet_competent" ? "text-red-800" : "text-slate-500"}>
                      {OUTCOME_LABEL[h.outcome] || (h.read_only ? "Signed" : "Draft")}
                    </span>
                    <span className="text-slate-500 text-[10px] ml-1">· {h.assessor_name || "—"}</span>
                    {h.read_only && <CheckCircle2 className="w-3 h-3 inline text-emerald-600 ml-1" />}
                  </div>
                  {h.read_only && (
                    <button onClick={() => print(h.id)} className="text-slate-500 hover:text-slate-800 text-[11px]" title="Print this assessment">
                      <Printer className="w-3 h-3" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
